/**
 * Snacklins — cart line subscription <-> one-time toggle.
 *
 * Switches a single cart line between its subscription selling plan and a one-time
 * purchase via POST /cart/change.js (selling_plan = id to subscribe, null to remove).
 * It does NOT morph anything itself: it dispatches a CartLinesUpdateEvent whose promise
 * resolves with the change.js `sections`, and Horizon's cart-items-component (whose
 * document-level listener fires for events it didn't originate) morphs the drawer/cart
 * section, updating totals, the cart badge, discount lines, and line indexes after merges.
 *
 * Mirrors the request/event shape of assets/component-cart-items.js#updateQuantity.
 */

import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class SnkCartPlanToggle extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const btn = /** @type {HTMLElement} */ (event.target).closest('[data-snk-switch]');
    if (!btn) return;
    event.preventDefault();
    if (this.dataset.busy === 'true') return;
    this.dataset.busy = 'true';

    const subscribe = btn.getAttribute('data-snk-switch') === 'subscribe';
    const planId = subscribe ? Number(this.dataset.planId) : null;

    // Re-render every cart section on the page (drawer + cart page if both present).
    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
    });

    const body = JSON.stringify({
      line: Number(this.dataset.line),
      quantity: Number(this.dataset.quantity),
      selling_plan: planId,
      sections: Array.from(sectionIds).join(','),
      sections_url: window.location.pathname,
    });

    // Optimistic disable; the section morph re-renders a fresh (enabled) list.
    this.closest('cart-items-component')?.classList.add('cart-items-disabled');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [],
        promise: deferred.promise,
      })
    );

    fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }))
      .then((response) => response.json())
      .then((json) => {
        if (json.errors) {
          deferred.reject(new Error(json.errors));
          this.dispatchEvent(new CartErrorEvent({ error: json.errors, code: 'INVALID' }));
          return;
        }
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(json),
          detail: {
            sections: json.sections,
            items: json.items,
            itemCount: json.item_count,
            source: 'snk-cart-plan-toggle',
            didError: false,
          },
        });
      })
      .catch((error) => {
        console.error(error);
        deferred.reject(error);
        this.dispatchEvent(
          new CartErrorEvent({ error: error?.message || 'Failed to update cart', code: 'SERVICE_UNAVAILABLE' })
        );
      })
      .finally(() => {
        this.dataset.busy = 'false';
      });
  };
}

if (!customElements.get('snk-cart-plan-toggle')) {
  customElements.define('snk-cart-plan-toggle', SnkCartPlanToggle);
}
